<template>
    <form @submit.prevent>
        <OfflineWarning class="offline-warning"></OfflineWarning>
        <div class="max-bid-row">
            <cool-checkbox-2>
                <input
                    type="checkbox"
                    data-ignorereplace="true"
                    :disabled="disabled"
                    @input="checkLoggedIn($event)"
                    @change="checkLoggedIn($event)"
                    v-model="maxBid"
                />
                <template #label
                    >&nbsp;{{
                        labels.frontendProductpage.auction.maxBid
                    }}</template
                >
            </cool-checkbox-2>
        </div>
        <CardManagementMain
            v-if="isLoggedIn && setup.enableMissingPaymentStrike"
            :terms-link="setup.termsUrl"
            @update:saved-card-status="savedCardStatus = $event"
            no-spinner
            ref="savedCardForm"
        ></CardManagementMain>
        <template v-if="isLoggedIn && phoneValidation != null">
            <PhoneValidationComp
                class="phone-validation-comp"
                :product-id="product.id"
                :sms-validation="phoneValidation.smsValidation"
                :sms-already-valid="phoneValidation.smsAlreadyValid"
                :reuse-sms-validation-id="
                    phoneValidation.reuseSmsValidationId ?? undefined
                "
                @update:smsValidationId="smsValidationId = $event"
                @update:validationPhone="validationPhone = $event"
                :existing-validation-id="
                    phoneValidation.existingValidationId ?? undefined
                "
                :existing-validation-phone="
                    phoneValidation.existingValidationPhone ?? undefined
                "
            ></PhoneValidationComp>
        </template>
        <div class="bid-row">
            <div class="bid-field-container">
                {{ currencySymbols[0] }}
                <cool-input :number-arrows="false">
                    <CurrencyFormattedInput
                        @focus="checkLoggedIn($event)"
                        @update:model-value="checkLoggedIn($event)"
                        v-model="bidAmount"
                        :disabled="disabled"
                        :min="(product.currentBid ?? 0) + 1"
                        :step="1"
                        @keypress="disableDecimalKeys($event)"
                        :class="{ 'bid-field': true, blitz: blitzBidField }"
                    />
                </cool-input>
                {{ currencySymbols[1] }}
            </div>
            <div class="button-column">
                <cool-button
                    theme="turqoise"
                    size="large"
                    full-width
                    :busy="loading"
                    :busy-size="32"
                >
                    <button
                        type="button"
                        class="submit-bid"
                        :disabled="disabled"
                        @click="checkLoggedIn($event) && submitBid()"
                    >
                        {{ labels.frontendProductpage.auction.bidButton }}
                    </button>
                </cool-button>
            </div>
            <div class="follow-container">
                <FollowButton
                    class="follow-comp"
                    :product-id="product.id"
                ></FollowButton>
            </div>
        </div>
        <fade-and-slide>
            <div class="error-row" v-if="errors.length > 0">
                <div v-for="error in errors">{{ error }}</div>
            </div>
        </fade-and-slide>
        <fade-and-slide>
            <div
                class="max-bid-message"
                v-if="
                    product.winningBid != null &&
                    product.winningBid.currentUser &&
                    product.winningBid.maxBid != null &&
                    product.winningBid.maxBid > 0
                "
            >
                {{
                    format(
                        labels.frontendProductpage.auction.currentMaxBid,
                        formatAmounts(product.winningBid.maxBid)
                    )
                }}
            </div>
        </fade-and-slide>
        <fade-and-slide>
            <div
                class="losing-bid-message"
                v-if="
                    product.hasBidOnProduct &&
                    product.winningBid != null &&
                    !product.winningBid.currentUser
                "
            >
                {{ labels.frontendProductpage.auction.losingBidMessage }}
            </div>
        </fade-and-slide>
        <fade-and-slide>
            <div
                class="bid-suggestion-row"
                v-if="suggestionAmounts == null || suggestionAmounts.length > 0"
            >
                <ul class="bid-suggestion-list">
                    <li
                        v-for="suggestionAmount in suggestionAmounts ?? [
                            -1, -2, -3,
                        ]"
                        :key="suggestionAmount"
                        class="bid-suggestion-item"
                    >
                        <button
                            class="bid-suggestion-button"
                            type="button"
                            :disabled="suggestionAmount <= 0"
                            @click="
                                checkLoggedIn($event) &&
                                    setAmount(
                                        (product.currentBid ?? 0) +
                                            suggestionAmount
                                    )
                            "
                            :aria-pressed="
                                bidAmount ==
                                (product.currentBid ?? 0) + suggestionAmount
                                    ? 'true'
                                    : 'false'
                            "
                        >
                            <template v-if="suggestionAmount > 0">
                                +{{ formatAmounts(suggestionAmount) }}
                            </template>
                            <template v-else> &nbsp; </template>
                        </button>
                    </li>
                </ul>
            </div>
        </fade-and-slide>
    </form>
</template>

<style scoped lang="scss">
@import "box.scss";

.phone-validation-comp {
    margin-bottom: 10px;
}
.bid-suggestion-list {
    display: flex;
    flex-direction: row;
    justify-content: left;
    align-items: baseline;
    list-style: none;
    padding: 0;
    margin: 0;
    margin-top: 10px;

    .bid-suggestion-item {
        margin-right: 12px;
        .bid-suggestion-button {
            cursor: pointer;
            color: black;
            border: 1px solid #dedede;
            background-color: white;
            border-radius: 300px;
            font-size: 12px;
            font-weight: 800;
            text-align: center;
            padding: 8px;
            min-width: 80px;
            transition: all ease-out 400ms;
            &[aria-pressed="true"] {
                box-shadow: inset 0px 0px 10px #bfbfbf,
                    inset 0px 0px 20px #979797;
                background: #e5d6d6;
                cursor: default;
            }
        }
    }
}

.offline-warning {
    margin-left: -12px;
    margin-right: -12px;
    margin-top: -12px;
    margin-bottom: 12px;
}

.max-bid-row {
    margin-bottom: 12px;
}
.bid-row {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;

    .bid-field-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: left;
        font-size: 20px;
        font-weight: 600;
        .bid-field {
            font-size: 20px;
            padding: 10px;
            text-align: center;
            font-weight: 600;
            border: 2px solid #272727;
            border-radius: 4px;
            background-color: #ffffff;
            width: 5em;
            transition: background-color 800ms
                cubic-bezier(0.22, 0.64, 0.56, 0.9);

            &.blitz {
                background-color: #74f099;
                transition: background-color 0ms linear;
            }
        }
    }

    .button-column {
        flex: 1;
        margin-left: 10px;
        margin-right: 10px;
    }
}

.losing-bid-message {
    @extend %box;
    margin-top: 10px;
    background-color: #d79696;
    font-weight: 600;
}

.error-row {
    @extend %box;
    margin-top: 10px;
    background-color: #d79696;
    font-weight: 600;
}

.max-bid-message {
    @extend %box;
    margin-top: 10px;
    background-color: #ddeac3;
    font-weight: 600;
}

.follow-container {
    align-self: center;
    .follow-comp {
        aspect-ratio: 1/1;
    }
}
.submit-bid {
    border-radius: 300px;
    padding: 15px;
}
</style>

<script setup lang="ts">
import { type PropType, computed, nextTick, ref, watch } from "vue";
import CoolCheckbox2 from "../../shared/CoolCheckbox2.vue";
import CoolInput from "../../shared/CoolInput.vue";
import CoolButton from "../../shared/CoolButton.vue";
import FollowButton from "../../frontend-productlist/product-list/list-item-component/common/follow-button.vue";
import CardManagementMain from "../../profile/card-management/bid-options/bid-options-main.vue";
import { type PhoneValidation, type AuctionProduct, type Setup } from "./types";
import {
    createCurrencyFormatter,
    getCurrencySymbol,
} from "../common/amount-format";
import axios from "axios";
import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import PhoneValidationComp from "../bidding/phone-validation/phone-validation.vue";

const formatter = createCurrencyFormatter();
const currencySymbols = getCurrencySymbol(formatter);
const props = defineProps({
    product: {
        type: Object as PropType<AuctionProduct>,
        required: true,
    },
    setup: {
        type: Object as PropType<Setup>,
        required: true,
    },
    phoneValidation: Object as PropType<PhoneValidation>,
});

const suggestionAmounts = ref<number[]>();
const maxBid = ref(true);
const errors = ref<string[]>([]);
const blitzBidField = ref(false);
const bidAmount = ref<number>();
const savedCardStatus = ref<"missing" | "ok">();
const validationPhone = ref<string>();
const smsValidationId = ref<string>();
const isLoggedIn = window.isAuthenticated() === true;
const loading = ref(false);
const openForBid = computed(
    () =>
        props.product.timeIsNotStarted === false &&
        props.product.timeTotalSeconds > 0
);
const disabled = computed(() => loading.value || !openForBid.value);

function formatAmounts(value: number) {
    return formatter.format(value);
}

function blitzField() {
    blitzBidField.value = true;
    requestAnimationFrame(() => {
        setTimeout(() => (blitzBidField.value = false), 100);
    });
}

function setAmount(v: number) {
    bidAmount.value = v;
    blitzField();
}

function disableDecimalKeys(e: Event) {
    if (!(e instanceof KeyboardEvent)) {
        return;
    }
    if (
        //["Comma","Period", "Slash", "NumpadAdd","NumpadSubtract", "Minus", "KeyE"].includes(e.code)
        [",", ".", "+", "e"].includes(e.key)
    ) {
        e.preventDefault();
    }
}

const savedCardForm = ref<InstanceType<typeof CardManagementMain>>();

import { loginPopup } from "../../../../legacy/loginPopup";
import labels from "../../shared/labels";
import OfflineWarning from "../product-specs/OfflineWarning.vue";
import { checkLoggedIn } from "./login";
import { format } from "../../../../shared/utils/string";
import { odendoTrackingService } from "../../../../tracking/tracking";
import CurrencyFormattedInput from "./currency-formatted-input.vue";

async function submitBid() {
    if (loading.value) return;
    loading.value = true;
    try {
        errors.value = [];
        const value = bidAmount.value;
        if (typeof value !== "number" || isNaN(value)) return;

        if (props.setup.enableMissingPaymentStrike) {
            if (savedCardStatus.value != "ok") {
                if (savedCardForm.value != null) {
                    (
                        savedCardForm.value as unknown as {
                            openPopup: (callback: () => void) => void;
                        }
                    ).openPopup(function () {
                        nextTick(() => submitBid());
                    });
                }
                return;
            }
        }

        const estimate = props.product.estimatedPrice;
        if (typeof estimate == "number" && estimate > 0 && value >= estimate) {
            const confirmLabel =
                labels.frontendProductpage.auction
                    .bidAboveEstimatedValueConfirmation;
            if (
                typeof confirmLabel === "string" &&
                confirmLabel.length > 0 &&
                !confirm(
                    labels.frontendProductpage.auction
                        .bidAboveEstimatedValueConfirmation
                )
            ) {
                return;
            }
        }
        const response = await axios.post<{
            success: boolean;
            noLogin?: true | undefined | null;
            message?: string | undefined | null;
        }>("/auctionProduct/BidJson", {
            productId: props.product.id,
            urlRef: btoa(location.href),
            maxBid: maxBid.value,
            savedCardStatus: savedCardStatus.value,
            amount: bidAmount.value,
            validationPhone: validationPhone.value,
            smsValidationId: smsValidationId.value,
            reuseSmsValidationId: props.phoneValidation?.reuseSmsValidationId,
        });

        const data = response.data;
        if (data.success !== true) {
            if (data.noLogin === true) {
                loginPopup();
                return;
            }
            if (data.message != null) {
                console.error(data.message);
                errors.value = [data.message];
            } else {
                errors.value = ["Unknown error"];
            }
        } else {
            odendoTrackingService.trackBid();
        }
    } finally {
        loading.value = false;
    }
}

watch(
    () => props.product.currentBid,
    async (newAmount) => {
        const response = await axios.get<number[]>("/ajax/GetBidSuggestions", {
            params: {
                amount: newAmount,
            },
        });
        suggestionAmounts.value = response.data;
        if (bidAmount.value == null) {
            bidAmount.value =
                (props.product.currentBid ?? 0) + suggestionAmounts.value[0];
        }
    },
    {
        immediate: true,
    }
);
</script>
